import {Controller} from "stimulus"

/**
 * Stimulus controller for the FWA debug profile button.
 *
 * Shows the UserFinancialProfile object that is used for filtering goals
 * and calculating scores. This is a debug/development tool.
 *
 * Usage in Hiccup (only shown when debug?=true):
 *   [:button {:data-controller "fwa-debug-profile"
 *             :data-fwa-debug-profile-run-uuid-value "..."
 *             :data-action "click->fwa-debug-profile#showProfile"}
 *    icon]
 */
export default class extends Controller {
  static get values() {
    return {
      runUuid: String
    }
  }

  connect() {
    console.log("fwa-debug-profile: controller connected")
    console.log("fwa-debug-profile: element at connect", this.element)
    console.log("fwa-debug-profile: runUuidValue at connect", this.runUuidValue)
    console.log("fwa-debug-profile: getAttribute at connect", this.element.getAttribute("data-fwa-debug-profile-run-uuid-value"))
  }

  async showProfile(event) {
    event.preventDefault()
    event.stopPropagation()

    // Debug: log all data attributes on the element
    console.log("fwa-debug-profile: element", this.element)
    console.log("fwa-debug-profile: element.outerHTML", this.element.outerHTML)
    console.log("fwa-debug-profile: runUuidValue", this.runUuidValue)
    console.log("fwa-debug-profile: getAttribute result", this.element.getAttribute("data-fwa-debug-profile-run-uuid-value"))

    // Try Stimulus value first, then fallback to manual attribute read
    let runUuid = this.runUuidValue
    if (!runUuid) {
      runUuid = this.element.getAttribute("data-fwa-debug-profile-run-uuid-value")
    }
    if (!runUuid) {
      alert("Error: No run-uuid available for debug profile. Check console for debug info.")
      return
    }

    try {
      const response = await fetch(`/api4/wellness/fwa/debug-profile?run-uuid=${encodeURIComponent(runUuid)}`)
      const data = await response.json()

      if (data.success && data.profile) {
        this.showProfileModal(data.profile, runUuid)
      } else {
        alert(`Error: ${data.error || "Failed to load profile"}`)
      }
    } catch (error) {
      console.error("Failed to fetch debug profile:", error)
      alert("Error: Failed to fetch debug profile. Check console for details.")
    }
  }

  showProfileModal(profile, runUuid) {
    // Create modal overlay
    const overlay = document.createElement("div")
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      z-index: 10000;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
    `

    // Create modal content
    const modal = document.createElement("div")
    modal.style.cssText = `
      background: white;
      border-radius: 8px;
      max-width: 900px;
      max-height: 90vh;
      width: 100%;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    `

    // Header
    const header = document.createElement("div")
    header.style.cssText = `
      padding: 16px 20px;
      border-bottom: 1px solid #e5e5e5;
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: #f8f8f8;
    `

    const title = document.createElement("h2")
    title.textContent = "UserFinancialProfile (Debug)"
    title.style.cssText = `
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: #333;
    `

    const uuidDisplay = document.createElement("div")
    uuidDisplay.textContent = `run-uuid: ${runUuid}`
    uuidDisplay.style.cssText = `
      margin-top: 4px;
      font-size: 11px;
      font-family: 'SF Mono', Monaco, monospace;
      color: #666;
      font-weight: normal;
    `

    const closeBtn = document.createElement("button")
    closeBtn.textContent = "×"
    closeBtn.style.cssText = `
      border: none;
      background: none;
      font-size: 28px;
      cursor: pointer;
      color: #666;
      padding: 0 8px;
      line-height: 1;
    `
    closeBtn.onclick = () => overlay.remove()

    const titleWrapper = document.createElement("div")
    titleWrapper.appendChild(title)
    titleWrapper.appendChild(uuidDisplay)

    header.appendChild(titleWrapper)
    header.appendChild(closeBtn)

    // Body with JSON
    const body = document.createElement("div")
    body.style.cssText = `
      padding: 20px;
      overflow-y: auto;
      flex: 1;
    `

    const pre = document.createElement("pre")
    pre.style.cssText = `
      margin: 0;
      font-family: 'SF Mono', Monaco, 'Inconsolata', 'Fira Code', monospace;
      font-size: 12px;
      line-height: 1.5;
      white-space: pre-wrap;
      word-break: break-all;
      color: #333;
    `
    pre.textContent = JSON.stringify(profile, null, 2)
    body.appendChild(pre)

    // Footer with copy button
    const footer = document.createElement("div")
    footer.style.cssText = `
      padding: 12px 20px;
      border-top: 1px solid #e5e5e5;
      background: #f8f8f8;
      display: flex;
      justify-content: flex-end;
      gap: 12px;
    `

    const downloadBtn = document.createElement("button")
    downloadBtn.textContent = "Download JSON"
    downloadBtn.style.cssText = `
      padding: 8px 16px;
      background: #004A76;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
    `
    downloadBtn.onclick = () => {
      try {
        const jsonStr = JSON.stringify(profile, null, 2)
        const blob = new Blob([jsonStr], {type: "application/json"})
        const url = URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = `fwa-profile-${runUuid}.json`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        downloadBtn.textContent = "Downloaded!"
        setTimeout(() => (downloadBtn.textContent = "Download JSON"), 2000)
      } catch (err) {
        console.error("Failed to download:", err)
      }
    }

    const copyBtn = document.createElement("button")
    copyBtn.textContent = "Copy JSON"
    copyBtn.style.cssText = `
      padding: 8px 16px;
      background: #004A76;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
    `
    copyBtn.onclick = async () => {
      try {
        await navigator.clipboard.writeText(JSON.stringify(profile, null, 2))
        copyBtn.textContent = "Copied!"
        setTimeout(() => (copyBtn.textContent = "Copy JSON"), 2000)
      } catch (err) {
        console.error("Failed to copy:", err)
      }
    }

    const resetBtn = document.createElement("button")
    resetBtn.textContent = "Reset Profile"
    resetBtn.style.cssText = `
      padding: 8px 16px;
      background: #dc2626;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
    `
    resetBtn.onclick = async () => {
      if (!confirm("WARNING: This will delete all goals for this profile. This action cannot be undone. Continue?")) {
        return
      }
      try {
        const response = await fetch("/api4/wellness/fwa/debug-reset-profile", {
          method: "POST",
          headers: {"Content-Type": "application/json"},
          body: JSON.stringify({"run-uuid": runUuid})
        })
        const data = await response.json()
        if (data.success) {
          alert(`Profile reset successfully. ${data.deleted_count} goals deleted.`)
          // Refresh the page to show updated state
          window.location.reload()
        } else {
          alert(`Failed to reset profile: ${data.error}`)
        }
      } catch (err) {
        console.error("Failed to reset profile:", err)
        alert("Error: Failed to reset profile. Check console for details.")
      }
    }

    footer.appendChild(resetBtn)
    footer.appendChild(downloadBtn)
    footer.appendChild(copyBtn)

    // Assemble modal
    modal.appendChild(header)
    modal.appendChild(body)
    modal.appendChild(footer)
    overlay.appendChild(modal)

    // Close on overlay click (but not modal click)
    overlay.onclick = (e) => {
      if (e.target === overlay) overlay.remove()
    }

    // Close on Escape key
    const handleKeydown = (e) => {
      if (e.key === "Escape") {
        overlay.remove()
        document.removeEventListener("keydown", handleKeydown)
      }
    }
    document.addEventListener("keydown", handleKeydown)

    document.body.appendChild(overlay)
  }
}
